import { z } from 'zod'
import { ParticipantKind } from './enums.js'
import { AuditEventType } from './entities.js'
import { SHA256_HEX_PATTERN } from './version.js'
import { MetricResultEnvelope } from './metrics.js'

/**
 * The remaining read families — SM5 (B7.2, B11.1, B14.2, B18.1; MCP-AC-130, MCP-AC-119).
 *
 * Three reads share this file because they share one discipline, stated once here:
 *
 * 1. **A read returns references, never content.** `P17-10` (which field types may appear in
 *    AI-host content, and which hosts are excluded) is **open**, so no shape in this file has a
 *    field for a document byte, a field *value*, an attachment name or a link. Where a caller
 *    could expect a value, the contract names the reason it is absent
 *    (`content_withheld: 'P17_10_HOST_CONTENT_DECISION_OPEN'`) instead of carrying a nullable
 *    field a host would read as "the value is empty".
 * 2. **An unpermitted object is absent, not blank.** A participant, a field or an agreement
 *    outside the grant answers exactly like an id that never existed (`OP_NOT_FOUND`), so ids stay
 *    non-enumerable (MCP-AC-014). A read that finds nothing *in scope* is honestly empty; a read
 *    *for* something out of scope is refused.
 * 3. **"We do not know" is a value.** `observed` is `OBSERVED` only where a server row proves the
 *    state, and `UNKNOWN` otherwise — the same rule `deriveAgreementStatus` applies to agreement
 *    status (MCP-AC-050) and `McpDeliveryReport` applies to delivery.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()
const Sha256Hex = z.string().regex(SHA256_HEX_PATTERN)

/* ------------------------------- analytics -------------------------------- */

export const getAnalyticsInput = z
  .object({
    days: z.union([z.literal(7), z.literal(30), z.literal(90)]).default(30),
  })
  .strict()
export type GetAnalyticsInput = z.infer<typeof getAnalyticsInput>

/** R10–R13 from the same connected metric registry as the sender Insights screen. */
export const McpAnalyticsResult = z
  .object({
    item_scope: z.enum(['ALL_PUBLISHED', 'SELECTED_ITEMS']),
    selected_item_count: z.number().int().nonnegative().nullable(),
    as_of: IsoDateTime,
    metrics: z
      .array(
        MetricResultEnvelope.safeExtend({
          metric_id: z.enum(['R10', 'R11', 'R12', 'R13']),
          value: z.number().nullable(),
        }),
      )
      .length(4),
  })
  .strict()
export type McpAnalyticsResult = z.infer<typeof McpAnalyticsResult>

/* ------------------------------- field data -------------------------------- */

/**
 * The bound on one field-data page. The accepted set is at most (participants × fields), so the
 * read pages rather than returning an unbounded list.
 */
export const MCP_FIELD_DATA_MAX_PAGE = 100

/** The two kinds of accepted datum the service actually holds. */
export const FIELD_DATA_CLASSES = ['RESPONSE_VALUE', 'ATTACHMENT'] as const
export const FieldDataClass = z.enum(FIELD_DATA_CLASSES)
export type FieldDataClass = z.infer<typeof FieldDataClass>

/**
 * What the service can name about an accepted datum without reading it: the object it lives in,
 * the digest the acceptance bound, and its size. The object is *never* fetched by this read — the
 * reference is the evidence, the bytes stay in the retention class that governs them.
 */
export const McpEvidenceReference = z
  .object({
    kind: z.enum(['RESPONSE_PACKAGE', 'ATTACHMENT_OBJECT', 'FINAL_ARTIFACT']),
    object_key: z.string().min(1).max(512),
    digest: Sha256Hex,
    bytes: z.number().int().positive(),
  })
  .strict()
export type McpEvidenceReference = z.infer<typeof McpEvidenceReference>

/**
 * A participant as the reads already name one: the same identity vocabulary
 * `signbento_get_envelope` uses (role, kind, position). The recipient id is deliberately absent —
 * a general read does not make participant ids enumerable.
 */
export const McpReadParticipant = z
  .object({
    participant_index: z.number().int().nonnegative(),
    role_id: z.string().min(1).max(64).nullable(),
    kind: ParticipantKind,
    is_sender: z.boolean(),
    order_index: z.number().int().nonnegative(),
  })
  .strict()
export type McpReadParticipant = z.infer<typeof McpReadParticipant>

export const getFieldDataInput = z
  .object({
    envelope_id: Id,
    /** Narrow to one participant by its stable index; an unknown index is refused, not empty. */
    participant_index: z.number().int().nonnegative().optional(),
    /** Narrow to one field id; a field with no accepted datum is refused, not blank. */
    field_id: z.string().min(1).max(64).optional(),
    cursor: z.string().min(1).max(512).optional(),
    limit: z.number().int().positive().max(MCP_FIELD_DATA_MAX_PAGE).default(50),
  })
  .strict()
export type GetFieldDataInput = z.infer<typeof getFieldDataInput>

/**
 * One accepted datum with its provenance. There is no `value` field anywhere in this shape: the
 * field a host would read as "the answer" does not exist, so a host cannot mistake an unexposed
 * value for an empty one.
 */
export const McpFieldDatum = z
  .object({
    /** The field the acceptance answered, or `null` for a whole-response commitment. */
    field_id: z.string().min(1).max(64).nullable(),
    class: FieldDataClass,
    participant: McpReadParticipant,
    /**
     * The revisions the acceptance bound: the agreement's own revision, and the participant
     * capability revision the response was submitted under. `capability` is `null` when the datum
     * is an attachment collected before the response commitment existed — the service holds no
     * revision for it rather than borrowing the current one.
     */
    revision: z
      .object({
        envelope: z.number().int().nonnegative(),
        capability: z.number().int().positive().nullable(),
      })
      .strict(),
    /** The frozen field manifest every acceptance in this agreement binds (REC-12). */
    field_manifest_digest: Sha256Hex,
    /** The server-recorded acceptance instant — never a client-reported one. */
    accepted_at: IsoDateTime,
    reference: McpEvidenceReference,
    /** Why no value accompanies the reference. A literal, so it cannot be softened into prose. */
    content_withheld: z.literal('P17_10_HOST_CONTENT_DECISION_OPEN'),
  })
  .strict()
export type McpFieldDatum = z.infer<typeof McpFieldDatum>

export const McpFieldDataResult = z
  .object({
    envelope_id: Id,
    envelope_revision: z.number().int().nonnegative(),
    field_manifest_digest: Sha256Hex,
    values: z.array(McpFieldDatum),
    /** Stated once at the result level rather than repeated on every datum. */
    values_withheld: z.literal('P17_10_HOST_CONTENT_DECISION_OPEN'),
    next_cursor: z.string().min(1).max(512).nullable(),
    bounds: z.object({ max_page: z.number().int().positive() }).strict(),
  })
  .strict()
export type McpFieldDataResult = z.infer<typeof McpFieldDataResult>

/* -------------------------------- audit ----------------------------------- */

/** The most events one page returns, and the widest window one request may ask for. */
export const MCP_AUDIT_MAX_EVENTS = 100
export const MCP_AUDIT_MAX_WINDOW_DAYS = 90
/**
 * The fold bound. The chain digest of an event is only computable from the chain's start, so the
 * read replays the stored sequence through the evidence package's own `appendAuditEvent`; past this
 * many events the read reports `folded: false` rather than spending an unbounded amount of work or
 * claiming a digest it did not compute.
 */
export const MCP_AUDIT_MAX_FOLD_EVENTS = 5000

/**
 * How the event is retained. Audit history is covered by the register's minimized-history class
 * (`minimized_history_retention_days`, PRD §14.2 "Minimized envelope and audit history"), which is
 * the same class the deletion disclosure names — one class, named in one place.
 */
export const MCP_AUDIT_RETENTION_CLASSES = ['MINIMIZED_SECURITY_EVIDENCE'] as const
export const McpAuditRetentionClass = z.enum(MCP_AUDIT_RETENTION_CLASSES)
export type McpAuditRetentionClass = z.infer<typeof McpAuditRetentionClass>

/**
 * How the event is redacted for this read.
 *
 * - `REFERENCE_ONLY`: every value in the stored payload is a reference — an id, a digest, a count,
 *   a machine code or a server instant — and is returned as stored.
 * - `CONTENT_WITHHELD`: the payload carried a value that is not a reference. The value is withheld
 *   and its key is named in `withheld_keys`, so a reader is told exactly what was removed instead
 *   of finding a silently blank field.
 */
export const MCP_AUDIT_REDACTION_CLASSES = ['REFERENCE_ONLY', 'CONTENT_WITHHELD'] as const
export const McpAuditRedactionClass = z.enum(MCP_AUDIT_REDACTION_CLASSES)
export type McpAuditRedactionClass = z.infer<typeof McpAuditRedactionClass>

export const getAuditEventsInput = z
  .object({
    envelope_id: Id,
    /** Inclusive lower bound on the server-recorded instant; the window is bounded below. */
    from: IsoDateTime.optional(),
    /** Exclusive upper bound on the server-recorded instant. */
    to: IsoDateTime.optional(),
    /** Resume after this sequence — the chain's own cursor. */
    after_sequence: z.number().int().nonnegative().optional(),
    limit: z.number().int().positive().max(MCP_AUDIT_MAX_EVENTS).default(50),
  })
  .strict()
  .superRefine((input, ctx) => {
    // The time-range bound is a schema rule rather than a check inside the read, so a window the
    // service will not serve is refused with the same field-level refusal any other bad argument
    // gets — and the bound travels in `bounds` so a host can see it before asking.
    if (input.from === undefined || input.to === undefined) return
    const span = Date.parse(input.to) - Date.parse(input.from)
    if (span <= 0 || span > MCP_AUDIT_MAX_WINDOW_DAYS * 86_400_000) {
      ctx.addIssue({
        code: 'custom',
        message: `the audit window is bounded to ${MCP_AUDIT_MAX_WINDOW_DAYS} days and must be forward`,
        path: ['to'],
      })
    }
  })
export type GetAuditEventsInput = z.infer<typeof getAuditEventsInput>

/**
 * The event's actor, in the vocabulary a host may see. A recipient actor is named by its role and
 * position, never by its recipient id — the same rule the participant list follows.
 */
export const McpAuditActor = z
  .object({
    kind: z.enum(['sender', 'recipient', 'system']),
    role_id: z.string().min(1).max(64).nullable(),
    participant_index: z.number().int().nonnegative().nullable(),
    /** The stored actor reference, present only when it is not a participant id. */
    reference: z.string().min(1).max(200).nullable(),
  })
  .strict()
export type McpAuditActor = z.infer<typeof McpAuditActor>

export const McpAuditEvent = z
  .object({
    sequence: z.number().int().nonnegative(),
    event_id: z.string().min(1).max(200),
    event_type: AuditEventType,
    /** The server-recorded instant — the only instant the chain digest hashes. */
    occurred_at: IsoDateTime,
    actor: McpAuditActor,
    retention_class: McpAuditRetentionClass,
    /** The register's window for the class, in days; `null` when it follows the record. */
    retention_window_days: z.number().int().positive().nullable(),
    redaction_class: McpAuditRedactionClass,
    /** The stored payload, as references. See `redaction_class` for what a value may be. */
    payload: z.record(z.string(), z.unknown()),
    /** The keys whose value was not a reference and is therefore withheld. */
    withheld_keys: z.array(z.string().min(1).max(64)),
    /** The chain link, recomputed by the evidence package; `null` when the chain was not folded. */
    event_digest: Sha256Hex.nullable(),
    prev_event_digest: Sha256Hex.nullable(),
  })
  .strict()
export type McpAuditEvent = z.infer<typeof McpAuditEvent>

export const McpAuditEventsResult = z
  .object({
    envelope_id: Id,
    events: z.array(McpAuditEvent),
    /** The sequence to resume after, or `null` when this page reached the chain's tip. */
    next_after_sequence: z.number().int().nonnegative().nullable(),
    window: z.object({ from: IsoDateTime.nullable(), to: IsoDateTime.nullable() }).strict(),
    bounds: z
      .object({
        max_events: z.number().int().positive(),
        max_window_days: z.number().int().positive(),
      })
      .strict(),
    chain: z
      .object({
        /** The page covers the chain from sequence 0 to its tip. */
        complete: z.boolean(),
        /** The chain was replayed, so the digests below are the chain's own. */
        folded: z.boolean(),
        length: z.number().int().nonnegative().nullable(),
        tip_digest: Sha256Hex.nullable(),
      })
      .strict(),
  })
  .strict()
export type McpAuditEventsResult = z.infer<typeof McpAuditEventsResult>

/* ---------------------------------- jobs ----------------------------------- */

/**
 * The three families that have a durable server-side home, and the one that does not.
 * `BATCH` is declared because the tool's vocabulary must name what it cannot yet answer: a batch
 * job has no producer in this build, so a batch lookup is refused rather than guessed.
 */
export const MCP_JOB_KINDS = [
  'PREPARATION',
  'WORKSPACE_INDEX',
  'DELIVERY_DISPATCH',
  'BATCH',
] as const
export const McpJobKind = z.enum(MCP_JOB_KINDS)
export type McpJobKind = z.infer<typeof McpJobKind>

/** The one state vocabulary every family is projected into, so a poll has one shape. */
export const MCP_JOB_STATES = [
  'QUEUED',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'REFUSED',
  'SUPERSEDED',
  'EXPIRED',
  'UNKNOWN',
] as const
export const McpJobState = z.enum(MCP_JOB_STATES)
export type McpJobState = z.infer<typeof McpJobState>

/** The terminal states — a poll of one of these never changes. */
export const MCP_JOB_TERMINAL_STATES = [
  'SUCCEEDED',
  'FAILED',
  'REFUSED',
  'SUPERSEDED',
  'EXPIRED',
] as const

/* ------------------------------- the Tasks extension ----------------------- */

/** The extension's identifier, as `server/discover` advertises it (SEP-2663). */
export const MCP_TASKS_EXTENSION = 'io.modelcontextprotocol/tasks'

/** The task id prefix. A handle is opaque to a host and minted by this service, never by a caller. */
export const MCP_TASK_HANDLE_PREFIX = 'signbento:task:v1'

/**
 * How long a task handle stays answerable, and how often a client should poll. Both are bounds the
 * service states rather than budgets it hides: a task older than the TTL is reported `EXPIRED`
 * with the handle still legible, and the interval is the value the server asks a client to honour.
 */
export const MCP_TASK_TTL_MS = 24 * 60 * 60 * 1000
export const MCP_TASK_POLL_INTERVAL_MS = 2_000
/** The number of polls a client should make inside one TTL before treating the task as unknown. */
export const MCP_TASK_MAX_POLLS = 60

export const TASK_STATUSES = [
  'working',
  'input_required',
  'completed',
  'failed',
  'cancelled',
] as const
export const TaskStatus = z.enum(TASK_STATUSES)
export type TaskStatus = z.infer<typeof TaskStatus>

/**
 * The task handle as `tasks/get` returns it. The shape is the extension's, not ours: a host that
 * speaks `io.modelcontextprotocol/tasks` must be able to read it without translation.
 */
export const McpTask = z
  .object({
    taskId: z.string().min(1).max(256),
    status: TaskStatus,
    statusMessage: z.string().min(1).max(200).optional(),
    createdAt: IsoDateTime,
    lastUpdatedAt: IsoDateTime,
    ttlMs: z.number().int().positive().nullable(),
    pollIntervalMs: z.number().int().positive().optional(),
    /** Present on `completed`: what the original request would have returned. */
    result: z.record(z.string(), z.unknown()).optional(),
    /** Present on `failed`: the JSON-RPC error the request failed with. */
    error: z
      .object({
        code: z.number().int(),
        message_key: z.string().min(1),
        recovery_action: z.string().min(1),
        retryable: z.boolean(),
      })
      .strict()
      .optional(),
    /** Present on `input_required`: the requests the task is waiting on. Empty in this build. */
    inputRequests: z.record(z.string(), z.unknown()).optional(),
    resultType: z.literal('complete'),
  })
  .strict()
export type McpTask = z.infer<typeof McpTask>

export const GetTaskParams = z.object({ taskId: z.string().min(1).max(256) }).strict()
export const UpdateTaskParams = z
  .object({
    taskId: z.string().min(1).max(256),
    inputResponses: z.record(z.string(), z.unknown()),
  })
  .strict()

/** `tasks/update`'s acknowledgement, exactly as the extension specifies it: an empty result. */
export const McpTaskUpdateAck = z.object({ resultType: z.literal('complete') }).strict()
export type McpTaskUpdateAck = z.infer<typeof McpTaskUpdateAck>

/**
 * A task handle for one job. The handle is derived from the job's own identity rather than stored,
 * so "the handle names a real job" is a property of the identifier instead of a second row that
 * could disagree with the first. `ref` may itself contain `:` (a dispatch ref is a logical message
 * id), so only the first four segments are fixed.
 */
export function taskHandleOf(kind: McpJobKind, ref: string): string {
  return `${MCP_TASK_HANDLE_PREFIX}:${kind}:${ref}`
}

export interface ParsedTaskHandle {
  readonly kind: McpJobKind
  readonly ref: string
}

/** Parse a handle, or `null` when it is not one this service minted. */
export function parseTaskHandle(handle: string): ParsedTaskHandle | null {
  const prefix = `${MCP_TASK_HANDLE_PREFIX}:`
  if (!handle.startsWith(prefix)) return null
  const rest = handle.slice(prefix.length)
  const separator = rest.indexOf(':')
  if (separator <= 0 || separator === rest.length - 1) return null
  const kind = rest.slice(0, separator)
  if (!(MCP_JOB_KINDS as readonly string[]).includes(kind)) return null
  return { kind: kind as McpJobKind, ref: rest.slice(separator + 1) }
}

/* ------------------------------ the job read ------------------------------- */

/**
 * A job read by its handle, or by the three facts a handle is made of. The second form exists so a
 * caller that already holds an item id and a version (from the workspace routes) does not have to
 * know the handle grammar; both forms reach one reader, so they cannot disagree.
 */
export const getJobInput = z
  .object({
    task_id: z.string().min(1).max(256).optional(),
    kind: McpJobKind.optional(),
    ref: z.string().min(1).max(200).optional(),
    version: z.number().int().nonnegative().optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    const byHandle = input.task_id !== undefined
    const byParts = input.kind !== undefined && input.ref !== undefined
    if (byHandle === byParts) {
      ctx.addIssue({
        code: 'custom',
        message: 'a job read takes either task_id or kind+ref, and never both',
        path: ['task_id'],
      })
    }
    if (input.version !== undefined && input.kind !== 'WORKSPACE_INDEX') {
      ctx.addIssue({
        code: 'custom',
        message: 'version belongs to a workspace index job',
        path: ['version'],
      })
    }
  })
export type GetJobInput = z.infer<typeof getJobInput>

/**
 * The job as the tool reports it. Every field is either a server fact or an explicit absence, and
 * the shape carries the Tasks extension's own handle so a host that supports the extension can
 * switch to `tasks/get` without relearning anything — and a host that does not loses no field.
 */
export const McpJobStatus = z
  .object({
    task_id: z.string().min(1).max(256),
    kind: McpJobKind,
    state: McpJobState,
    /** `OBSERVED` when a server row proves the state; `UNKNOWN` when the service holds none. */
    observed: z.enum(['OBSERVED', 'UNKNOWN']),
    terminal: z.boolean(),
    /** The machine outcome: the family's own code, never prose, and never invented. */
    outcome: z
      .object({ code: z.string().min(1).max(120).nullable(), retryable: z.boolean().nullable() })
      .strict(),
    attempt: z.number().int().nonnegative().nullable(),
    created_at: IsoDateTime.nullable(),
    updated_at: IsoDateTime.nullable(),
    finished_at: IsoDateTime.nullable(),
    retry_due_at: IsoDateTime.nullable(),
    /** Bounded counters only — pages, bytes, elapsed. Never content, never a query. */
    progress: z.record(z.string(), z.unknown()).nullable(),
    poll: z
      .object({
        interval_ms: z.number().int().positive(),
        next_poll_after_ms: z.number().int().nonnegative(),
        max_polls: z.number().int().positive(),
        ttl_ms: z.number().int().positive(),
      })
      .strict(),
    /** Which surface this read stands in for. A literal, so a host can branch on it. */
    fallback_of: z.literal(MCP_TASKS_EXTENSION),
  })
  .strict()
export type McpJobStatus = z.infer<typeof McpJobStatus>
